import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild, SimpleChanges, OnInit, OnChanges, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { AppIcons } from '../../../../shared/app.icons';
import { AppSettings } from '../../../../shared/app.settings';
import { MITableComponent } from '../../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../../shared/models/country';
import type { Language } from '../../../../shared/models/language';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../../shared/services/config.service';
import type { EntityCountsResponse, EntityList } from '../../../../shared/models/entity.models';
import type { ListResponse } from '../../../../shared/models/listResponse.models';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { AddProtocolProductComponent } from './add-protocol-product/add-protocol-product.component';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';

interface ColumnDefinition {
  attributeCode: string;
  attributeIndex: number;
  field: string;
  header: string;
  attributeId?: string;
  [key: string]: unknown;
}

interface FilterField {
  attributeId: string;
  attributeValue: string;
  [key: string]: unknown;
}

interface AttributeData {
  tabs: Array<{
    groups: Array<{
      fields: Array<{
        attributeCode: string;
        attributeId: string;
        [key: string]: unknown;
      }>;
    }>;
  }>;
  [key: string]: unknown;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  [key: string]: unknown;
}

interface TableDataItem {
  [key: string]: unknown;
}

interface ButtonItem {
  label: string;
  icon: string;
  command: () => void;
  [key: string]: unknown;
}

interface FilterResponse {
  responseBody: string;
  [key: string]: unknown;
}

interface MetaDataResponse {
  advancedFilter: FilterResponse;
  sort: FilterResponse;
  defaultTableView: FilterResponse;
  [key: string]: unknown;
}

interface CurrentRowData {
  [key: string]: unknown;
}

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss',
  standalone: true,
  imports: [
    TabViewModule,
    TranslateModule,
    MITableComponent,
    BreadcrumbModule,
    ButtonModule,
    DialogModule,
    AddProtocolProductComponent,
    AccessProviderDirective
  ]
})
export class ProductsComponent implements OnInit, OnChanges {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @ViewChild('addProtocolProduct') addProtocolProduct: AddProtocolProductComponent;
  @Input() isActive: boolean = false;
  @Input() isSiteReady: boolean = false;
  @Input() siteEntityId: string;
  @Input() isAllowToAddProduct: boolean = false;
  @Input() isChangeReasonNotRequired: boolean = false;
  @Input() isEditNotAllowed: boolean = false;
  @Input() isEditNotAllowedSpecificStatus: boolean = false;
  @Output() saveSuccess = new EventEmitter<void>();

  miIcons = AppIcons;
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns: ColumnDefinition[] = [];
  cols: ColumnDefinition[] = [];
  moduleName = AppSettings.ENTITY_CODE.PROTOCOL_PRODUCT;
  filterList: ColumnDefinition[] = [];
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels: Record<string, string> = {};
  filterAttributeLabels: Record<string, string> = {};
  listColumns: ColumnDefinition[] = [];
  allColumns: ColumnDefinition[] = [];
  pagination: PaginationData;
  totalRecords: number = 0;
  tableData: TableDataItem[] = [];
  buttons: ButtonItem[] = [];
  country: Country;
  language: Language;
  entityCount: EntityCountsResponse;
  itemsPerPage: number;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    actionStatus: ''
  };
  routePath: MenuItem[] = [];
  deleteSiteMsg: string;
  deleteSiteHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  filterFieldsArray: FilterField[] = [];
  isReady: boolean = false;
  attributeData: AttributeData;
  data: unknown;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  currentRow: CurrentRowData;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader: string;
  reviewHeader: string;
  btnUpdate: string;
  entityCreatedAtAttributeId: string;
  regionGeofences: unknown[] = [];
  zoneIds: string[] = [];
  isEntity = true;
  showAddDialog: boolean = false;
  showAddProtocolProductDialog: boolean = false;
  productId: string;
  protocolEntityId: string;
  protocolProductId: string;
  trialId: string;
  productStatusAttributeId: string;
  productStatusObject: any;

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private messageService: MessageService,
    private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService,
    private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef,
    private loaderService: LoaderService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.route.queryParams.subscribe(param => {
      this.trialId = param?.id
    });
    if (this.isAllowToAddProduct) {
      this.isAllowToAddProduct = this.permissionAccessService.hasAccess('PROTOCOL_PRODUCTS', '', 'ADD', '');
    }
    this.initializeLabels();
    this.initializeRowItems();
    this.initializeModuleHeaderSecondaryBtnMenuItems();
    this.initializeOtherVariablesAndAPI();
    this.getMetaData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isActive && changes.isActive.currentValue === true && !this.data) {
      this.initializeLabels();
      this.initializeRowItems();
      this.initializeModuleHeaderSecondaryBtnMenuItems();
      this.initializeOtherVariablesAndAPI();
      this.getMetaData();
    }
  }

  getMetaData() {
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res: MetaDataResponse) => {

      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.setProductFilterView(advancedFilter);
      }


      const sort = JSON.parse(res.sort.responseBody);
      const entityCreatedAt = sort.tabs[0].groups[0].fields.find((field: { attributeCode: string }) => field.attributeCode === AppSettings.ENTITY_CREATED_AT);
      this.entityCreatedAtAttributeId = entityCreatedAt.attributeId;
      if (this.entityCreatedAtAttributeId) {
        // this.entityData.defaultSortColumn = this.entityCreatedAtAttributeId;
        this.entityData.defaultSortType = AppSettings.DESC_SORT_ORDER;
      }

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setProductTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort);
    });
  }

  setProductFilterView(filterResponse: AttributeData) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse) as unknown as Record<string, string>;
    this.filterList = this.cs.getColumns(filterResponse) as unknown as ColumnDefinition[];
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
    this.entityData.filters = [{
      attributeId: this.filterList.find(field => field.attributeCode === 'protocol_entity_id')?.attributeId,
      attributeValue: this.trialId
    }]

    if (this.configService.isSponsor() || this.configService.isSiteUser()) {
      const productStatusFilter = this.filterList.find(filter => filter.attributeCode === 'product_status') as any;
      if (productStatusFilter && productStatusFilter.presetValues) {
        productStatusFilter.presetValues = productStatusFilter.presetValues.filter(
          (preset: any) => preset.labelKey !== 'Requested'
        );

        const protocolStatusAttributeId = productStatusFilter.attributeId;
        const protocolStatusValues = productStatusFilter.presetValues.map((preset: any) => preset.labelKey);
        this.productStatusObject = {
          attributeId: protocolStatusAttributeId,
          attributeValue: protocolStatusValues
        };
        this.entityData.filters.push(this.productStatusObject);
      }
    }
  }

  private getMultiApiPayload() {
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_PRODUCT}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_PRODUCT}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_PRODUCT}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      }
    ]
  }

  onAdd() {
    this.showAddDialog = true;
  }

  onAddProtocolProduct() {
    this.showAddProtocolProductDialog = true;
  }

  onClosePopup(event: boolean) {
    if (event) {
      this.searchEntity();
    }
    this.showAddDialog = false;
  }

  private initializeRowItems() {
    const isEdit = this.permissionAccessService.hasAccess('PROTOCOL_PRODUCTS', '', 'EDIT', '');
    const isActivate = this.permissionAccessService.hasAccess('PROTOCOL_PRODUCTS', '', 'ACTIVE', '');
    if (this.configService.isAdmin()) {
      this.rowItems = [
        ...(isEdit ? [{
          label: this.cs.getLabel('product.lbl_edit'),
          icon: `${AppIcons.EDIT_2} wh-16`,
          iconClass: 'bulk-update-icon',
          value: 'edit',
          command: () => {
            this.update();
          }
        }] : []),
        { separator: true },
        ...(isActivate ? [{
          label: this.cs.getLabel('product.lbl_activate'),
          icon: `${AppIcons.CIRCLE_CHECKED} wh-16`,
          iconClass: 'bulk-update-icon',
          value: 'active',
          command: () => {
            this.onUpdateStatus('Active', this.cs.getLabel('product.lbl_activate'));
          }
        }] : []),
        ...(isActivate ? [{
          label: this.cs.getLabel('product.lbl_deactivate'),
          icon: `${AppIcons.ACTIVATE} wh-16`,
          iconClass: 'bulk-update-icon',
          value: 'deactive',
          command: () => {
            this.onUpdateStatus('Inactive', this.cs.getLabel('product.lbl_deactivate'));
          }
        }] : []),
      ];
    }
  }

  onUpdateStatus(status: string, acceptLabelText: string) {
    const isActive = status === 'Active';

    this.confirmationService.confirm({
      header: this.cs.getLabel(isActive ? 'protocol_product.lbl_activate_confirmation' : 'protocol_product.lbl_deactivate_confirmation'),
      message: this.cs.getLabel(isActive ? 'protocol_product.msg_activate_confirmation' : 'protocol_product.msg_deactivate_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: acceptLabelText,
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const productData = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.productStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.STATUS_UPDATE_VIEW,
        };

        this.entityService.saveAttributeData(productData.entityCode, this.currentRow.id as string, productData).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel('protocol_product.lbl_status_updated_successfully'),
            });
            setTimeout(() => {
              this.searchEntity();
            }, 300);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.cs.getLabel('product.lbl_failed_status')
            });
          }
        });
      },
      reject: () => {
      }
    });
  }

  private initializeLabels() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.routePath = [{ label: "Products", routerLink: 'app/products' }];
  }

  private initializeOtherVariablesAndAPI() {
    this.btnLabel = this.cs.getLabel('Add Product');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteSiteMsg = this.cs.getLabel('product.message.confirm_delete_msg');
    this.deleteSiteHeaderMsg = this.cs.getLabel('product.message.confirm_delete_header');
  }

  setProductTableView(res: unknown) {
    this.isLoading = false;
    this.data = res;
    this.attributeLabels = this.cs.getAttributeLabels(this.data) as unknown as Record<string, string>;
    this.allColumns = this.cs.getColumns(this.data) as unknown as ColumnDefinition[];
    this.allColumns = (this.cs.getColumns(this.data) as unknown as ColumnDefinition[]).filter(column => {
      const excludedColumns = ['entity_created_at'];
      if (this.configService.isSiteUser()) {
        excludedColumns.push('unit_price', 'unit_cost');
      }
      if (this.configService.isSponsor()) {
        excludedColumns.push('unit_cost');
      }
      return !excludedColumns.includes(column.attributeCode);
    });
    this.productStatusAttributeId = this.allColumns.find(column => column.attributeCode === 'product_status')?.attributeId as string;
    this.searchEntity();
    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `protocol_product.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
    });

    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  searchEntity() {
    try {
      this.loaderService.show();
      this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;

      this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL_PRODUCT, this.entityData, false)
        .subscribe({
          next: (res: ListResponse) => {
            this.isSiteReady = true;
            if (!res?.data?.length) {
              this.tableData = [];
              this.totalRecords = 0;
              return;
            }
            this.listColumns = res.data[0]?.values as unknown as ColumnDefinition[] || [];
            this.pagination = res.pagination as unknown as PaginationData;
            this.totalRecords = res.count || 0;
            this.tableData = [];

            this.tableData = res.data.map(data => ({
              id: data.id,
              ...(data.values || {})
            }));
          },
          error: () => {
            this.tableData = [];
            this.totalRecords = 0;
          },
          complete: () => {
            this.loaderService.hide();
          }
        });
    } catch (error) {
      this.loaderService.hide();
      this.tableData = [];
      this.totalRecords = 0;
    }
  }

  onSortData(sortColumnDetails) {
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  onPageChange(event: { first: number; rows: number }) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event: number) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  onFilterValueChange(event: Record<string, unknown>) {
    const vData = event;
    this.entityData.filters = [];
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value as string;
      if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue.length > 0) {
        return item.attributeCode;
      }
    }), (element) => {
      if (element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });
    this.entityData.offset = (event.first as number) || 0;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }

  onSearchValueChanges(event: string) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  setCurrentRowData(event: CurrentRowData) {
    this.currentRow = event;

    if (event.deleted) {
      this.rowItems?.forEach(item => {
        if (!item.separator) {
          item.disabled = true;
        }
      });
      return;
    }
    this.rowItems?.forEach(item => {
      switch (item.value) {
        case 'edit':
          item.disabled = this.isEditNotAllowed || (event.product_status as { Rejected: string })?.Rejected === 'Rejected' || (event.product_status as { Archive: string })?.Archive === 'Archived' || (event.product_status as { Inactive: string })?.Inactive === 'Inactive';
          break;
        case 'active':
          item.disabled = (event.product_status as { Active: string })?.Active === 'Active' || this.isEditNotAllowed || (event.product_status as { Requested: string })?.Requested === 'Requested' || (event.product_status as { Rejected: string })?.Rejected === 'Rejected' || (event.product_status as { Archive: string })?.Archive === 'Archived';
          break;
        case 'deactive':
          item.disabled = (event.product_status as { Inactive: string })?.Inactive === 'Inactive' || this.isEditNotAllowed || (event.product_status as { Requested: string })?.Requested === 'Requested' || (event.product_status as { Rejected: string })?.Rejected === 'Rejected' || (event.product_status as { Archive: string })?.Archive === 'Archived';
          break;
      }
    });
  }

  update() {
    this.onUpdateProduct(this.currentRow.id as string);
  }

  onUpdateProduct(productId: string) {
    this.showAddProtocolProductDialog = true;
    this.protocolProductId = productId;
  }

  onToggleOverlayForAdd() {
    this.showAddProtocolProductDialog = false;
    this.protocolProductId = null;
  }

  openAddDialog() {
    this.showAddDialog = true;
  }

  onDialogVisibleChange(value: boolean) {
    this.showAddDialog = value;
  }

  onDialogShow() {
    this.showAddProtocolProductDialog = true;
  }

  onDialogClose() {
    this.showAddDialog = false;
    this.showAddProtocolProductDialog = false;
  }

  onSaveSuccess() {
    this.showAddDialog = false;
    this.showAddProtocolProductDialog = false;
    this.searchEntity();
    this.saveSuccess.emit();
  }

  private initializeModuleHeaderSecondaryBtnMenuItems() {
    this.moduleHeaderSecondaryBtnMenuItems = [
      {
        label: this.cs.getLabel('product.lbl_add_product'),
        icon: `${AppIcons.ADD} wh-16`,
        command: () => {
          this.onAdd();
        }
      }
    ];
  }
}

